import { Overlay, LAYER } from '@design/Overlay'
import { useEffect, useState } from 'react'
import { loadDocumentPages, type DocumentPage } from '@core/attachments'
import { Spinner } from '@design/components'
import { ZoomableImage } from './ZoomableImage'

/**
 * The original document, full screen.
 *
 * docs/spec.md: the original is always one tap away, at full quality. It is
 * the thing the AI summary is a convenience for, and for anything legal or
 * financial it is the only authority. So this screen is deliberately plain —
 * no annotations over the image, nothing that could be mistaken for part of
 * the document.
 */
export function DocumentViewer({
  documentId,
  title,
  onClose,
}: {
  documentId: string
  title?: string
  onClose: () => void
}) {
  const [pages, setPages] = useState<DocumentPage[] | null>(null)
  const [index, setIndex] = useState(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    loadDocumentPages(documentId)
      .then((p) => alive && setPages(p))
      .catch((e) => alive && setError(String(e?.message ?? e)))
    return () => {
      alive = false
    }
  }, [documentId])

  // The back gesture people actually expect from a full-screen image.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setIndex((i) => i + 1)
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const page = pages?.[Math.min(index, (pages?.length ?? 1) - 1)]
  const total = pages?.length ?? 0

  return (
    <Overlay layer={LAYER.document} className="!bg-black">
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between text-white">
        <button onClick={onClose} className="px-2 py-2 -ml-2 text-white/90" aria-label="Sluiten">
          ← Terug
        </button>
        <span className="text-sm text-white/70 truncate max-w-[55%]">{title ?? 'Document'}</span>
        <span className="text-sm text-white/70 tabular-nums">
          {total > 1 ? `${Math.min(index, total - 1) + 1}/${total}` : ''}
        </span>
      </header>

      <div className="flex-1 min-h-0">
        {error && (
          <div className="h-full grid place-items-center px-6 text-center">
            <div>
              <p className="text-white mb-1">Kon het document niet openen</p>
              <p className="text-white/60 text-sm">{error}</p>
            </div>
          </div>
        )}

        {!error && !pages && <Spinner label="Document laden…" />}

        {!error && pages && pages.length === 0 && (
          <div className="h-full grid place-items-center px-6 text-center">
            <p className="text-white/70">Er is geen afbeelding bij dit document.</p>
          </div>
        )}

        {page && page.contentType === 'application/pdf' ? (
          // Safari renders a PDF in a frame but gives no page controls and no
          // pinch-zoom worth having, so the escape hatch below is not a
          // fallback — it is how you actually read a long one.
          <div className="h-full flex flex-col">
            <iframe
              key={page.attachmentId}
              src={page.url}
              title={title ?? 'Document'}
              className="flex-1 w-full bg-white"
            />
            <a
              href={page.url}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 text-center text-sm text-white/80 py-3 underline underline-offset-2"
            >
              Openen in een nieuw venster
            </a>
          </div>
        ) : page ? (
          <ZoomableImage
            key={page.attachmentId}
            src={page.url}
            alt={`Pagina ${page.pageNumber}`}
          />
        ) : null}
      </div>

      {total > 1 && (
        <nav className="shrink-0 pad-safe-bottom px-4 py-3 flex items-center justify-center gap-2">
          {pages!.map((p, i) => (
            <button
              key={p.attachmentId}
              onClick={() => setIndex(i)}
              aria-label={`Pagina ${p.pageNumber}`}
              aria-current={i === index ? 'true' : undefined}
              className={`h-2 rounded-full transition-all ${
                i === index ? 'w-6 bg-white' : 'w-2 bg-white/40'
              }`}
            />
          ))}
        </nav>
      )}
    </Overlay>
  )
}
